import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Field, inputCls, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { X } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal reutilizable para crear un movimiento de finanzas (ingreso/egreso).
 * Props:
 *  - defaultType (opcional): "expense" | "income" — default "expense".
 *  - defaultDescription (opcional): pre-llena la descripción (útil desde vista de servicio).
 *  - defaultCategory (opcional): pre-llena la categoría (ej. nombre del cliente).
 *  - defaultCustomerId / defaultServiceId / defaultQuoteId / defaultTechnician (opcionales):
 *    vínculos operacionales pre-llenados (invisibles al usuario, se guardan al crear).
 *  - onClose: callback al cerrar.
 *  - onCreated: callback tras crear.
 */
export default function FinanceEntryForm({
  defaultType = "expense",
  defaultDescription = "",
  defaultCategory = "",
  defaultCustomerId = null,
  defaultServiceId = null,
  defaultQuoteId = null,
  defaultTechnician = null,
  onClose,
  onCreated,
}) {
  const [data, setData] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: defaultDescription,
    amount: 0,
    type: defaultType,
    category: defaultCategory,
    notes: "",
    paid_date: "",
    customer_id: defaultCustomerId,
    service_id: defaultServiceId,
    quote_id: defaultQuoteId,
    technician: defaultTechnician,
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...data, amount: Number(data.amount) };
      if (!payload.paid_date) delete payload.paid_date;
      // Limpia vínculos operacionales null para no ensuciar el DB.
      for (const k of ["customer_id", "service_id", "quote_id", "technician"]) {
        if (!payload[k]) delete payload[k];
      }
      const { data: created } = await api.post("/finance/entries", payload);
      if (file && created.id) {
        const form = new FormData();
        form.append("file", file);
        await api.post(`/finance/entries/${created.id}/receipt`, form, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Movimiento agregado");
      onCreated && onCreated(created);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg border-t-4 border-[#FF5A00]" data-testid="finance-new-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="font-display font-bold text-xl">Nuevo movimiento</div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Fecha *">
            <input type="date" required data-testid="finance-date-input" value={data.date} onChange={(e) => setData({ ...data, date: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Tipo *">
            <select data-testid="finance-type-select" value={data.type} onChange={(e) => setData({ ...data, type: e.target.value })} className={inputCls}>
              <option value="expense">Egreso</option>
              <option value="income">Ingreso</option>
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Descripción *">
              <input required data-testid="finance-desc-input" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} className={inputCls} />
            </Field>
          </div>
          <Field label="Monto (CLP) *">
            <input type="number" min="1" required data-testid="finance-amount-input" value={data.amount} onChange={(e) => setData({ ...data, amount: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Categoría">
            <input placeholder="Ej: Repuestos, Sueldos, Cliente X" value={data.category} onChange={(e) => setData({ ...data, category: e.target.value })} className={inputCls} />
          </Field>
          {data.type === "expense" && (
            <div className="md:col-span-2">
              <Field label="Fecha de pago (opcional)" hint="Déjala vacía si registro y pago ocurren el mismo día.">
                <input type="date" data-testid="finance-paid-date-input" value={data.paid_date} onChange={(e) => setData({ ...data, paid_date: e.target.value })} className={inputCls} />
              </Field>
            </div>
          )}
          {data.type === "expense" && (
            <div className="md:col-span-2">
              <Field label="Comprobante (imagen o PDF)" hint="Se guarda en almacenamiento seguro">
                <input type="file" accept="image/*,application/pdf" data-testid="finance-receipt-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block text-sm" />
              </Field>
            </div>
          )}
          <div className="md:col-span-2">
            <Field label="Notas">
              <textarea rows={2} value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })} className={inputCls} />
            </Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="finance-save-btn">{saving ? "Guardando…" : "Guardar"}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}
